import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useAbility } from 'providers/casl'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { EditSchema } from '../../schemas/editSchema'
import { toDTO } from './api/mapper'
import { UPDATE_DOMAIN } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateDomain } from './types'

export const useUpdateDomain = (): UseUpdateDomain => {
  const ability = useAbility()
  const { t } = useTranslation('features/domain')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<EditSchema>>({})

  const { isPending, mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN(ability),
    onCompleted: async (data: Data) => {
      const {
        updateDomain: { errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
    queriesToInvalidate: ['domain'],
  })

  useEffect(() => {
    setLoading(isPending)
  }, [isPending])

  const updateDomain = (data: EditSchema): void => {
    mutateAsync({
      input: toDTO(data, ability),
    })
  }

  return { errors, updateDomain }
}
